import React, { ChangeEvent } from "react";
import {
    Button,
    Checkbox,
    Chip,
    createStyles,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    Input,
    InputLabel,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    SelectProps,
    Switch,
    Tab,
    Tabs,
    TextField,
    Theme,
    Typography,
    withStyles,
    WithStyles
} from "@material-ui/core";
import { Archive, Clear, Delete, Save } from "@material-ui/icons";
import { enqueueSnackbar } from "notistack";
import { ReactImageGalleryItem } from "react-image-gallery";
import { Temporal } from "temporal-polyfill";
import {
    DateTimeLocalFormat,
    parseAuctionInstant,
    skippedOrRepeatedTimeMessage,
    toInstant,
    toWallTime,
    visitorTimeZone
} from "../../../helpers/auctionDateTime";
import IErrorInfo from "../../../models/account/IErrorInfo";
import IAuctionDetail from "../../../models/auction/IAuctionDetail";
import { FileType } from "../../../models/files/FileType";
import { IBidder } from "../../../models/parcel/IBidder";
import ICreateUpdateParcel from "../../../models/parcel/ICreateUpdateParcel";
import { IParcelDetail } from "../../../models/parcel/IParcelDetail";
import Constants from "../../../models/utility/Constants";
import { LoadState } from "../../../models/utility/LoadState";
import CurrencyField from "../CurrencyField";
import DateTimePicker from "../DateTimePicker";
import FileUploader from "../FileUploader";
import FormErrorWrapper from "../FormErrorWrapper";
import ImageCarousel from "../ImageCarousel";
import LoaderButton from "../LoaderButton";
import AuditTimestamps from "./AuditTimestamps";
import ParcelBidLogList from "./ParcelBidLogList";
import ParcelLocationForm from "./ParcelLocationForm";
import PictureLoader from "./PictureLoader";

export type ParcelEditContainerProps = WithStyles<typeof styles> & {
    auction: IAuctionDetail;
    onFileDelete: (
        entityId: string,
        fileType: FileType,
        setErrorState: (errors: IErrorInfo[]) => void
    ) => (
        fileName: string
    ) => (e: React.MouseEvent | null) => Promise<IErrorInfo[]>;
    onFileReorder: (
        entityId: string,
        fileType: FileType
    ) => (
        fileName: string,
        newIndex: number
    ) => (e: React.MouseEvent | null) => Promise<IErrorInfo[]>;
    onFileUpload: (
        entityId: string,
        fileType: FileType,
        setErrorState: (errors: IErrorInfo[]) => void
    ) => (files: File[]) => Promise<IErrorInfo[]>;
    onParcelCreate: (url: string, parcel: ICreateUpdateParcel) => void;
    onParcelDelete: (url: string, parcelId: string) => void;
    onParcelRemoveClick: (e: React.MouseEvent) => void;
    onParcelUpdate: (
        parcelId: string,
        parcel: ICreateUpdateParcel,
        bidders: string[],
        shouldUpdateBidders: boolean
    ) => void;
    onParcelArchive: (parcelId: string, isArchiving: boolean) => void;
    parcel: IParcelDetail | null;
    parcelIndex: number | null;
    resetParcelSaveErrors: () => void;
    savingNewParcel: boolean;
    savingNewParcelErrors: IErrorInfo[];
    url: string;
    lastRegularParcelCloseDateTime: string;
};

interface IParcelEditContainerState {
    createUpdateParcel: ICreateUpdateParcel;
    deleteClicked: boolean;
    isAuctionStartDateValid: boolean;
    isAuctionEndDateValid: boolean;
    isSlugEmpty: boolean;
    isLastAcceptedBidEmpty: boolean;
    isStartingBidEmpty: boolean;
    isBidIncrementEmpty: boolean;
    formErrors: IErrorInfo[];
    fileErrors: IErrorInfo[];
    pictureErrors: IErrorInfo[];
    selectedTab: number;
    approvedEnBlocBidders: string[];
    startDateValue: Temporal.PlainDateTime | null;
    endDateValue: Temporal.PlainDateTime | null;
    startDateSkippedMessage: string | null;
    endDateSkippedMessage: string | null;
    [x: string]:
        | boolean
        | number
        | string
        | string[]
        | Temporal.PlainDateTime
        | ICreateUpdateParcel
        | IErrorInfo[]
        | null;
}

const styles = (theme: Theme) =>
    createStyles({
        allowedBidderMenuItem: {
            backgroundColor: "transparent !important"
        },
        allowedBidderSelect: {
            height: "36px",
            margin: theme.spacing(1),
            marginTop: "15px",
            minWidth: 120,
            width: "100%"
        },
        buttonWrapper: { marginTop: theme.spacing(2) },
        carouselWrapper: {
            display: "flex",
            justifyContent: "center",
            marginTop: theme.spacing(3),
            maxHeight: 450,
            position: "relative",
            width: "100%"
        },
        chip: {
            margin: 2
        },
        chips: {
            display: "flex",
            flexWrap: "wrap"
        },
        deleteButton: {
            "&:hover": { backgroundColor: theme.palette.error.dark },
            backgroundColor: theme.palette.error.main,
            color: theme.palette.error.contrastText,
            marginLeft: theme.spacing(2)
        },
        detailItemText: {
            [theme.breakpoints.down("xs")]: { fontSize: 11 },
            lineHeight: 1.5,
            color: "white"
        },
        exportButton: {
            float: "right"
        },
        fileDelete: {
            position: "absolute",
            right: 0,
            top: 0
        },
        fileDeleteBtn: {
            "&:hover": { color: theme.palette.error.main },
            color: theme.palette.text.secondary,
            padding: 0
        },
        fileDeleteIcon: {
            filter: "drop-shadow(1px 1px 1px black)",
            msFilter: "drop-shadow(1px 1px 1px black)",
            webkitFilter: "drop-shadow(1px 1px 1px black)"
        },
        fileWrapper: {
            minWidth: 180,
            paddingTop: theme.spacing(3),
            position: "relative"
        },
        helperText: { margin: 0, padding: 0 },
        helperTextContainer: { marginTop: 12, marginBottom: 12 },
        image: {
            height: "auto",
            maxHeight: 450,
            maxWidth: "100%",
            width: "auto"
        },
        imageDelete: {
            padding: theme.spacing(),
            position: "absolute",
            right: 0,
            top: 0
        },
        imageDeleteBtn: {
            "&:hover": { color: theme.palette.error.main },
            color: theme.palette.common.white,
            padding: 0
        },
        imageDeleteIcon: {
            filter: "drop-shadow(2px 2px 2px black)",
            msFilter: "drop-shadow(2px 2px 2px black)",
            webkitFilter: "drop-shadow(2px 2px 2px black)"
        },
        imageWrapper: { position: "relative" },
        multifileContainer: {
            display: "block",
            overflow: "auto"
        },
        paperRoot: {
            backgroundColor: "#eee",
            marginBottom: theme.spacing(4),
            marginTop: theme.spacing(4),
            padding: theme.spacing(4)
        },
        paperTabDetails: {
            marginLeft: 4,
            marginRight: 4,
            padding: 24
        },
        parcelDetailIcon: {
            [theme.breakpoints.down("xs")]: { fontSize: 40 },
            fontSize: 50
        },
        parcelDetailItem: {
            display: "flex",
            flexDirection: "column",
            justifyContent: "center"
        },
        parcelDetailItemIcon: {
            alignItems: "flex-end",
            display: "flex",
            justifyContent: "center"
        },
        parcelDetailItemText: {
            alignItems: "flex-start",
            display: "block",
            flex: "1 0 35%",
            justifyContent: "center",
            overflowWrap: "break-word"
        },
        removeButton: { marginLeft: theme.spacing(2) },
        tab: {
            [theme.breakpoints.down("xs")]: { fontSize: 11 },
            backgroundColor: theme.palette.background.paper,
            marginLeft: 4,
            marginRight: 5,
            marginTop: 3
        },
        tabBar: { backgroundColor: "#eee" },
        tabNotSelected: { backgroundColor: "#eee" },
        tabSelected: { boxShadow: theme.shadows[2] },
        toggleSwitch: {
            marginTop: "25px"
        },
        uploadWrapper: { marginTop: theme.spacing() }
    });

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250
        }
    }
};

class ParcelEditContainer extends React.PureComponent<
    ParcelEditContainerProps,
    IParcelEditContainerState
> {
    constructor(props: ParcelEditContainerProps) {
        super(props);

        let parcelName = "";
        if (props.parcelIndex !== null) {
            parcelName = "Parcel " + props.parcelIndex.toString();
        }

        const createUpdateParcel = this.buildCreateUpdateParcel(parcelName);

        this.state = {
            createUpdateParcel,
            deleteClicked: false,
            fileErrors: [],
            formErrors: [],
            isAuctionEndDateValid: true,
            isAuctionStartDateValid: true,
            isSlugEmpty: true,
            isBidIncrementEmpty: false,
            isLastAcceptedBidEmpty: false,
            isStartingBidEmpty: false,
            approvedEnBlocBidders: [],
            pictureErrors: [],
            selectedTab: 0,
            startDateValue: toWallTime(this.getParcelStartDateTime()),
            endDateValue: toWallTime(this.getParcelEndDateTime()),
            startDateSkippedMessage: null,
            endDateSkippedMessage: null
        };
    }

    public componentWillMount() {
        this.props.resetParcelSaveErrors();
    }

    public componentDidUpdate(prevProps: ParcelEditContainerProps) {
        // Show snackbar if save was successful
        if (
            prevProps.parcel &&
            prevProps.parcel.isSaving &&
            this.props.parcel &&
            !this.props.parcel.isSaving &&
            (!this.props.parcel.savingErrors ||
                this.props.parcel.savingErrors.length === 0)
        ) {
            if (this.state.deleteClicked) {
                this.openErrorSnackbar();
            } else {
                this.openSuccessSnackbar();
            }
            return;
        } else if (
            this.props.parcel &&
            this.props.parcel.savingErrors &&
            this.props.parcel.savingErrors.length > 0 &&
            this.state.deleteClicked
        ) {
            this.setState({ deleteClicked: false });
            return;
        }

        // set up initial form on first load
        const parcel = this.props.parcel;
        if (
            parcel &&
            parcel !== prevProps.parcel &&
            (!parcel.savingErrors || parcel.savingErrors.length === 0) &&
            !parcel.isSaving
        ) {
            const createUpdateParcel = this.buildCreateUpdateParcel();

            this.setState({
                createUpdateParcel,
                startDateValue: this.toEditableWallTime(
                    parcel.auctionStartDate
                ),
                endDateValue: this.toEditableWallTime(parcel.auctionEndDate),
                approvedEnBlocBidders:
                    parcel.approvedEnBlocBidders?.map((p) => p.id) ?? []
            });
        }
    }

    public render() {
        const classes = this.props.classes;

        const isParcelActive = this.props.parcel
            ? this.props.parcel.isActive
            : false;

        return (
            <Paper className={classes.paperRoot} elevation={2}>
                <Grid container={true} spacing={2}>
                    <Grid item={true} xs={12}>
                        <Typography component="h6" variant="h6">
                            {!this.props.parcel
                                ? "New Parcel"
                                : `${this.props.parcel.name} ${
                                      !isParcelActive ? " (Archived)" : ""
                                  }`}
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container={true} spacing={2}>
                    <Grid item={true} xs={12}>
                        <Paper square={true} elevation={0}>
                            <Tabs
                                className={classes.tabBar}
                                value={this.state.selectedTab}
                                indicatorColor="primary"
                                textColor="primary"
                                onChange={this.onTabChange}
                                variant="fullWidth"
                            >
                                <Tab
                                    className={`${classes.tab} ${
                                        this.state.selectedTab === 0
                                            ? classes.tabSelected
                                            : classes.tabNotSelected
                                    }`}
                                    label="Details"
                                />
                                <Tab
                                    className={`${classes.tab} ${
                                        this.state.selectedTab === 1
                                            ? classes.tabSelected
                                            : classes.tabNotSelected
                                    }`}
                                    label="Locations"
                                />
                                <Tab
                                    className={`${classes.tab} ${
                                        this.state.selectedTab === 2
                                            ? classes.tabSelected
                                            : classes.tabNotSelected
                                    }`}
                                    label="Bid Log"
                                />
                            </Tabs>
                        </Paper>
                    </Grid>
                </Grid>

                {this.state.selectedTab === 0 && this.renderDetailsTab()}
                {this.state.selectedTab === 1 && this.renderParcelLocationTab()}
                {this.state.selectedTab === 2 && this.renderBidLogTab()}
            </Paper>
        );
    }

    private renderDetailsTab = () => {
        const classes = this.props.classes;

        const auctionLive = this.props.auction && this.props.auction.isLive;
        const auctionClosed =
            this.props.auction && this.props.auction.isCompleted;

        const isSaving = !this.props.parcel
            ? !!this.props.savingNewParcel
            : !!this.props.parcel.isSaving;

        const parcelLive = !this.props.parcel
            ? false
            : this.props.parcel.isLive;
        const parcelWithinRunTime = this.isParcelWithinRunTime(
            this.props.parcel
        );
        const parcelCompleted = !this.props.parcel
            ? false
            : this.props.parcel.isCompleted;
        const parcelActive = !this.props.parcel
            ? false
            : this.props.parcel.isActive;
        const errors = (
            !this.props.parcel
                ? this.props.savingNewParcelErrors
                : this.props.parcel.savingErrors || []
        ).concat(this.state.formErrors);
        const enBloc = this.state.createUpdateParcel.enBloc;

        const images = [] as ReactImageGalleryItem[];
        if (
            this.props.parcel &&
            this.props.parcel.pictureMetadata &&
            this.props.parcel.pictureMetadata.length > 0
        ) {
            for (let i = 0; i < this.props.parcel.pictureMetadata.length; i++) {
                const image = this.props.parcel.pictureMetadata[i];
                const thumbnail = this.props.parcel.thumbnailMetadata[i];

                images.push({
                    original: image.url,
                    originalAlt: image.fileName,
                    thumbnail: thumbnail ? thumbnail.url : ""
                });
            }
        }

        const isSynchronizedAuction = this.props.auction.isSynchronized;

        const regularParcelsHaveNotClosedYet =
            this.regularParcelsHaveNotClosedYet();
        const shouldDisableDateTimePickers =
            parcelLive ||
            parcelCompleted ||
            (enBloc ? regularParcelsHaveNotClosedYet : isSynchronizedAuction);
        const shouldRenderNoSaleSwitch =
            auctionClosed && this.props.parcel != null;

        return (
            <Paper
                className={classes.paperTabDetails}
                square={true}
                elevation={2}
            >
                <Grid container={true} spacing={4}>
                    <Grid item={true} xs={12}>
                        <FormErrorWrapper
                            errors={errors.map((e) => e.description)}
                            errorMessage="The following errors occurred during save:"
                        />
                    </Grid>
                </Grid>
                <Grid container={true} spacing={4}>
                    <Grid item={true} sm={2}>
                        <FormControlLabel
                            disabled={auctionLive || auctionClosed}
                            className={classes.toggleSwitch}
                            label="'En Bloc'"
                            control={
                                <Switch
                                    disabled={false}
                                    name="enBloc"
                                    checked={
                                        this.state.createUpdateParcel.enBloc
                                    }
                                    value="enBloc"
                                    onChange={this.onParcelPropertyChange(true)}
                                    color="primary"
                                />
                            }
                        />
                    </Grid>
                    {enBloc && (
                        <Grid item={true} xs={8}>
                            <FormControl
                                className={classes.allowedBidderSelect}
                                disabled={!auctionLive || auctionClosed}
                            >
                                <InputLabel htmlFor="select-multiple-chip">
                                    Allowed Bidders
                                    {auctionLive
                                        ? ""
                                        : " (can be set when Sale is live)"}
                                </InputLabel>
                                <Select
                                    multiple={true}
                                    value={this.state.approvedEnBlocBidders}
                                    onChange={this.onBidderSelectChange}
                                    input={<Input id="select-multiple-chip" />}
                                    renderValue={this.renderSelectedBidder}
                                    MenuProps={MenuProps}
                                >
                                    {this.props.parcel &&
                                        [
                                            ...(this.props.parcel
                                                .possibleEnBlocBidders ?? [])
                                        ]
                                            .sort(this.sortBidders)
                                            .map((bidder) => (
                                                <MenuItem
                                                    key={bidder.id}
                                                    value={bidder.id}
                                                    className={
                                                        classes.allowedBidderMenuItem
                                                    }
                                                >
                                                    <Checkbox
                                                        checked={
                                                            this.state.approvedEnBlocBidders.indexOf(
                                                                bidder.id
                                                            ) > -1
                                                        }
                                                        color="primary"
                                                    />
                                                    <ListItemText
                                                        primary={bidder.name}
                                                        secondary={bidder.email}
                                                    />
                                                </MenuItem>
                                            ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    )}
                </Grid>
                <Grid container={true} spacing={4}>
                    <Grid item={true} xs={6}>
                        <TextField
                            disabled={auctionLive || auctionClosed}
                            margin="normal"
                            fullWidth={true}
                            required={true}
                            label="Name"
                            name="name"
                            value={this.state.createUpdateParcel.name}
                            onChange={this.onParcelPropertyChange(false)}
                        />
                    </Grid>
                    <Grid item={true} xs={6}>
                        <TextField
                            disabled={auctionLive || auctionClosed}
                            margin="normal"
                            fullWidth={true}
                            required={true}
                            label="Parcel URL"
                            name="slug"
                            helperText="Parcel URL cannot exceed 20 characters. Only letters, numbers, or dashes are allowed."
                            value={this.state.createUpdateParcel.slug}
                            onChange={this.onParcelPropertyChange(false)}
                            type="text"
                            inputProps={{ maxLength: 20 }}
                            onKeyDown={(e) => {
                                // Only allow alphanumeric input
                                if (!/[a-zA-Z0-9-]+/.test(e.key)) {
                                    e.preventDefault();
                                }
                            }}
                        />
                    </Grid>
                </Grid>
                <Grid container={true} spacing={4}>
                    <Grid item={true} xs={12} sm={6}>
                        <DateTimePicker
                            disabled={shouldDisableDateTimePickers}
                            error={!this.state.isAuctionStartDateValid}
                            fullWidth={true}
                            helperText={this.renderDatePickerHelperText(
                                this.state.startDateSkippedMessage
                            )}
                            label="Start Date"
                            margin="normal"
                            min={this.getParcelStartDateMin()}
                            name="auctionStartDate"
                            onChange={this.onParcelStartDateChange}
                            required={true}
                            value={this.state.startDateValue}
                        />
                    </Grid>
                    <Grid item={true} xs={12} sm={6}>
                        <DateTimePicker
                            disabled={shouldDisableDateTimePickers}
                            error={!this.state.isAuctionEndDateValid}
                            fullWidth={true}
                            helperText={this.renderDatePickerHelperText(
                                this.state.endDateSkippedMessage
                            )}
                            label="End Date"
                            margin="normal"
                            min={this.getParcelEndDateMin()}
                            name="auctionEndDate"
                            onChange={this.onParcelEndDateChange}
                            required={true}
                            value={this.state.endDateValue}
                        />
                    </Grid>
                </Grid>
                <Grid container={true} spacing={4}>
                    {this.state.createUpdateParcel.isPrivateSale ? (
                        <Grid item={true} xs={12} sm={6}>
                            <CurrencyField
                                disabled={auctionLive}
                                required={
                                    this.state.createUpdateParcel.isPrivateSale
                                }
                                label="Sale Amount"
                                name="lastAcceptedBid"
                                value={
                                    this.state.createUpdateParcel
                                        .lastAcceptedBid
                                }
                                onChange={this.onParcelPropertyChange(
                                    false,
                                    true
                                )}
                            />
                        </Grid>
                    ) : (
                        <>
                            <Grid item={true} xs={12} sm={6}>
                                <CurrencyField
                                    disabled={auctionLive || auctionClosed}
                                    required={true}
                                    label="Starting Bid"
                                    name="startingBid"
                                    value={
                                        this.state.createUpdateParcel
                                            .startingBid
                                    }
                                    onChange={this.onParcelPropertyChange(
                                        false,
                                        true
                                    )}
                                />
                            </Grid>
                            <Grid item={true} xs={12} sm={6}>
                                <CurrencyField
                                    disabled={auctionLive || auctionClosed}
                                    required={true}
                                    label="Bid Increment"
                                    name="bidIncrement"
                                    value={
                                        this.state.createUpdateParcel
                                            .bidIncrement
                                    }
                                    onChange={this.onParcelPropertyChange(
                                        false,
                                        true
                                    )}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
                <Grid container={true} spacing={4}>
                    {!this.props.parcel && (
                        <Grid item={true} xs={12}>
                            <Typography
                                component="div"
                                variant="body2"
                                align="center"
                                color="textSecondary"
                            >
                                You must save the parcel first before uploading
                                images.
                            </Typography>
                        </Grid>
                    )}

                    {this.props.parcel && (
                        <>
                            <Grid item={true} xs={12} sm={12} md={6}>
                                <FormHelperText>Carousel Images</FormHelperText>
                                <div className={classes.uploadWrapper}>
                                    <FileUploader
                                        disabled={auctionLive}
                                        entityId={this.props.auction.auctionId}
                                        fileType={FileType.ParcelPicture}
                                        fileUploadFn={this.props.onFileUpload(
                                            this.props.parcel.parcelId,
                                            FileType.ParcelPicture,
                                            this.setPictureErrorState
                                        )}
                                    />
                                </div>
                            </Grid>
                            <Grid item={true} xs={12} sm={12} md={6}>
                                <div className={classes.carouselWrapper}>
                                    <PictureLoader
                                        visible={
                                            this.props.parcel
                                                .pictureMetadataLoadState ===
                                            LoadState.Loading
                                        }
                                    />
                                    {images.length > 0 &&
                                        this.state.selectedTab === 0 && (
                                            <ImageCarousel
                                                images={images}
                                                showThumbnailControls={
                                                    !auctionLive
                                                }
                                                showThumbnails={true}
                                                fixedRatio={true}
                                                maxWidth={400}
                                                backButtonText="Go Back To Parcel Details"
                                                deleteFn={this.props.onFileDelete(
                                                    this.props.parcel.parcelId,
                                                    FileType.ParcelPicture,
                                                    this.setPictureErrorState
                                                )}
                                                reorderFn={this.props.onFileReorder(
                                                    this.props.parcel.parcelId,
                                                    FileType.ParcelPicture
                                                )}
                                            />
                                        )}
                                </div>
                            </Grid>
                        </>
                    )}
                </Grid>
                {shouldRenderNoSaleSwitch && (
                    <Grid container={true} spacing={4}>
                        <Grid item={true} xs={12}>
                            <FormControlLabel
                                className={classes.toggleSwitch}
                                label="No Sale"
                                control={
                                    <Switch
                                        name="isNoSale"
                                        checked={this.props.parcel.isNoSale}
                                        color="primary"
                                        onChange={() => undefined}
                                        onClick={(e) => e.preventDefault()}
                                    />
                                }
                            />
                            <FormHelperText>
                                If 'No Sale' is true, this parcel will be hidden
                                from the bidding page.
                            </FormHelperText>
                        </Grid>
                    </Grid>
                )}
                <Grid container={true} spacing={4}>
                    <Grid item={true} xs={6}>
                        <div className={classes.buttonWrapper}>
                            {
                                <LoaderButton
                                    loading={isSaving}
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    onClick={this.onParcelSave}
                                >
                                    <Save />
                                    &nbsp;
                                    {!this.props.parcel ? "Create" : "Update"}
                                </LoaderButton>
                            }
                            {this.props.parcel && !parcelWithinRunTime && (
                                <LoaderButton
                                    className={classes.deleteButton}
                                    loading={isSaving}
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    onClick={this.onParcelArchive}
                                >
                                    <Archive />
                                    &nbsp;
                                    {parcelActive ? "Archive" : "Unarchive"}
                                </LoaderButton>
                            )}
                            {!auctionLive &&
                                !auctionClosed &&
                                this.props.parcel && (
                                    <LoaderButton
                                        className={classes.deleteButton}
                                        loading={isSaving}
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        onClick={this.onParcelDelete}
                                    >
                                        <Delete />
                                        &nbsp;Delete
                                    </LoaderButton>
                                )}
                            {!auctionLive &&
                                !auctionClosed &&
                                !this.props.parcel && (
                                    <Button
                                        className={classes.removeButton}
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        onClick={this.props.onParcelRemoveClick}
                                    >
                                        <Clear />
                                        &nbsp;Remove
                                    </Button>
                                )}
                        </div>
                    </Grid>
                    {this.props.parcel && this.props.parcel && (
                        <Grid item={true} xs={6}>
                            <div className={classes.buttonWrapper}>
                                <AuditTimestamps
                                    createdBy={this.props.parcel.createdBy}
                                    createdOn={this.props.parcel.createdOn}
                                    modifiedBy={this.props.parcel.modifiedBy}
                                    modifiedOn={this.props.parcel.modifiedOn}
                                />
                            </div>
                        </Grid>
                    )}
                </Grid>
            </Paper>
        );
    };

    private isParcelWithinRunTime = (parcel: IParcelDetail | null): boolean => {
        if (parcel) {
            const now = new Date();
            const startDate = new Date(parcel.auctionStartDate);
            const endDate = new Date(parcel.auctionEndDate);

            return endDate >= now && startDate <= now;
        }

        return false;
    };

    private sortBidders = (a: IBidder, b: IBidder) => {
        if (a.name === b.name) {
            return a.email < b.email ? -1 : 1;
        }
        return a.name < b.name ? -1 : 1;
    };
    private renderSelectedBidder = (selected: SelectProps["value"]) => {
        if (!this.props.parcel) {
            return <></>;
        }
        const bidders = this.props.parcel.possibleEnBlocBidders ?? [];

        if (!Array.isArray(selected)) {
            throw new Error("Selected bidders must be a string array.");
        }

        return (
            <div className={this.props.classes.chips}>
                {selected.map((value) => {
                    const bidder = bidders.filter((p) => p.id === value)[0];
                    return (
                        <Chip
                            key={bidder.id}
                            label={bidder.name}
                            className={this.props.classes.chip}
                        />
                    );
                })}
            </div>
        );
    };

    private renderBidLogTab = () => {
        const classes = this.props.classes;

        if (!this.props.parcel) {
            throw new Error("Parcel must be set to render bid log tab.");
        }

        return (
            <Paper
                className={classes.paperTabDetails}
                square={true}
                elevation={2}
            >
                <ParcelBidLogList
                    auctionClosed={this.props.parcel.isCompleted}
                    auctionEndDate={this.props.parcel.auctionEndDate}
                    parcelId={this.props.parcel.parcelId}
                    parcelName={this.props.parcel.name}
                    auctionName={this.props.auction.name}
                    startingBid={this.props.parcel.startingBid || 0}
                    lastAcceptedBidOverride={
                        this.props.parcel.lastAcceptedBidOverride
                    }
                    onUpdateClicked={this.onOverrideLastAcceptedBid}
                    isSaving={this.props.parcel.isSaving}
                />
            </Paper>
        );
    };

    private renderParcelLocationTab = () => {
        // This should really be under the details tab, but it's a seperate tab for now to avoid a bunch of
        //   refactoring of both this container and the details tab. - Jerremy Lewis 2024-10-09

        const isDisabled =
            this.props.auction?.isLive ||
            this.props.auction?.isCompleted ||
            this.props.parcel?.enBloc;

        return (
            <Paper
                className={this.props.classes.paperTabDetails}
                square={true}
                elevation={2}
            >
                {this.props.parcel?.parcelId ? (
                    <ParcelLocationForm
                        parcelId={this.props.parcel.parcelId}
                        isDisabled={isDisabled}
                    />
                ) : (
                    <Grid item={true} xs={12}>
                        <Typography
                            component="div"
                            variant="body2"
                            align="center"
                            color="textSecondary"
                        >
                            You must save the parcel first before adding
                            locations.
                        </Typography>
                    </Grid>
                )}
            </Paper>
        );
    };

    private setPictureErrorState = (pictureErrors: IErrorInfo[]) =>
        this.setState({ pictureErrors });

    private renderDatePickerHelperText = (skippedMessage?: string | null) => {
        const classes = this.props.classes;

        let messages: string[] = [
            `Time currently shown in ${visitorTimeZone()}`,
            `Format: ${DateTimeLocalFormat}`
        ];

        const isEnbloc = this.state.createUpdateParcel.enBloc;
        if (isEnbloc) {
            if (this.regularParcelsHaveNotClosedYet()) {
                messages = [
                    "This field is disabled because the regular parcels have not closed yet.",
                    `'En Bloc' is set to start ${Constants.EnBlocStartWindowMinutes} minutes after the last regular parcel closes.`
                ];
            }
        } else {
            const isSynchronized = this.props.auction.isSynchronized;
            if (isSynchronized) {
                messages = [
                    "This field is disabled because this parcel is part of a synchronized sale."
                ];
            } else if (skippedMessage) {
                messages = [skippedMessage];
            }
        }

        return (
            <span className={classes.helperTextContainer}>
                {messages.map((message) => (
                    <p className={classes.helperText} key={message}>
                        {message}
                    </p>
                ))}
            </span>
        );
    };

    private onTabChange = (e: React.ChangeEvent<{}>, value: number) =>
        this.setState({ selectedTab: value });

    private getParcelStartDateMin = (): Temporal.PlainDateTime | null => {
        if (this.props.parcel) {
            if (this.props.parcel.isLive || this.props.parcel.isCompleted) {
                return this.toEditableWallTime(
                    this.props.parcel.auctionStartDate
                );
            }
        }
        return toWallTime(Temporal.Now.instant());
    };

    private getParcelEndDateMin = (): Temporal.PlainDateTime | null =>
        this.state.startDateValue ?? toWallTime(Temporal.Now.instant());

    private onParcelStartDateChange = (
        value: Temporal.PlainDateTime | null,
        isValid: boolean
    ) => {
        if (!isValid) {
            this.setState({ isAuctionStartDateValid: false });
            return;
        }

        if (!value) {
            this.setState((s) => ({
                createUpdateParcel: {
                    ...s.createUpdateParcel,
                    auctionStartDate: null
                } as ICreateUpdateParcel,
                startDateValue: null,
                isAuctionStartDateValid: true,
                startDateSkippedMessage: null
            }));
            return;
        }

        const instant = toInstant(value);
        if (!instant) {
            this.setState({
                isAuctionStartDateValid: false,
                startDateSkippedMessage: skippedOrRepeatedTimeMessage()
            });
            return;
        }

        this.setState((s) => {
            const isEndValid =
                !s.endDateSkippedMessage &&
                (!s.endDateValue ||
                    Temporal.PlainDateTime.compare(s.endDateValue, value) >= 0);

            return {
                createUpdateParcel: {
                    ...s.createUpdateParcel,
                    auctionStartDate: instant.toString()
                } as ICreateUpdateParcel,
                startDateValue: value,
                isAuctionStartDateValid: true,
                isAuctionEndDateValid: isEndValid,
                startDateSkippedMessage: null
            };
        });
    };

    private onParcelEndDateChange = (
        value: Temporal.PlainDateTime | null,
        isValid: boolean
    ) => {
        if (!isValid) {
            this.setState({ isAuctionEndDateValid: false });
            return;
        }

        if (!value) {
            this.setState((s) => ({
                createUpdateParcel: {
                    ...s.createUpdateParcel,
                    auctionEndDate: null
                } as ICreateUpdateParcel,
                endDateValue: null,
                isAuctionEndDateValid: true,
                endDateSkippedMessage: null
            }));
            return;
        }

        const instant = toInstant(value);
        if (!instant) {
            this.setState({
                isAuctionEndDateValid: false,
                endDateSkippedMessage: skippedOrRepeatedTimeMessage()
            });
            return;
        }

        this.setState((s) => {
            const isValid =
                !s.startDateValue ||
                Temporal.PlainDateTime.compare(value, s.startDateValue) >= 0;
            return {
                createUpdateParcel: {
                    ...s.createUpdateParcel,
                    auctionEndDate: instant.toString()
                } as ICreateUpdateParcel,
                endDateValue: value,
                isAuctionEndDateValid: isValid,
                endDateSkippedMessage: null
            };
        });
    };

    private onBidderSelectChange = (
        event: React.ChangeEvent<{
            name?: string;
            value: unknown;
        }>
    ) => {
        if (!Array.isArray(event.target.value)) {
            throw new Error("Selected bidders must be a string array.");
        }

        this.setState((s) => {
            return {
                ...s,
                approvedEnBlocBidders: event.target.value as string[]
            };
        });
    };

    private onOverrideLastAcceptedBid = (value: number | null) => {
        const createUpdateParcel = {
            ...this.state.createUpdateParcel,
            lastAcceptedBidOverride: value
        } as ICreateUpdateParcel;
        this.setState({ createUpdateParcel });

        if (!this.props.parcel) {
            throw new Error(
                "Parcel must be defined to override last accepted bid"
            );
        }

        if (this.validateForm()) {
            this.props.onParcelUpdate(
                this.props.parcel.parcelId,
                createUpdateParcel,
                this.state.approvedEnBlocBidders,
                false
            );
        }
    };

    private onParcelPropertyChange =
        (isCheckbox = false, isNumber = false) =>
        (event: ChangeEvent<HTMLInputElement>) => {
            const name = event.target.name;
            const value = isCheckbox
                ? (event.target.checked as boolean)
                : isNumber
                  ? parseInt(event.target.value, 10)
                  : (event.target.value as string);
            interface ICorrespondingProperty {
                bidIncrement: string;
                startingBid: string;
                [key: string]: string;
            }

            const isEnbloc = name === "enBloc";
            if (isEnbloc) {
                // 'En Bloc' parcel changes affect the auction start and end dates
                //  - If changing to 'En Bloc', set to 15 minutes after the last regular parcel closes
                //  - If changing to regular, set to default opening time
                //  - If already 'En Bloc', allow the admin to override the dates

                let enblocStartDate: Temporal.Instant;
                let enblocEndDate: Temporal.Instant;

                const isChangingToEnbloc = value === true;
                if (isChangingToEnbloc) {
                    enblocStartDate = this.getEnblocStartDateTime();
                    enblocEndDate = this.getEnblocEndDateTime();
                } else {
                    enblocStartDate = this.getParcelStartDateTime();
                    enblocEndDate = this.getParcelEndDateTime();
                }

                return this.setState({
                    createUpdateParcel: {
                        ...this.state.createUpdateParcel,
                        auctionStartDate: enblocStartDate.toString(),
                        auctionEndDate: enblocEndDate.toString(),
                        enBloc: !!value
                    },
                    startDateValue: toWallTime(enblocStartDate),
                    endDateValue: toWallTime(enblocEndDate)
                });
            }

            if (
                ["bidIncrement", "lastAcceptedBid", "startingBid"].filter(
                    (val) => val === name
                ).length > 0
            ) {
                const correspondingProperty: ICorrespondingProperty = {
                    bidIncrement: "isBidIncrementEmpty",
                    lastAcceptedBid: "isLastAcceptedBidEmpty",
                    startingBid: "isStartingBidEmpty"
                };
                const correspondingField = correspondingProperty[
                    name
                ] as string;

                if (
                    value === "" ||
                    (isNumber && isNaN(parseInt(event.target.value, 10)))
                ) {
                    this.setState({
                        [correspondingField]: true,
                        createUpdateParcel: {
                            ...this.state.createUpdateParcel,
                            [name]: value
                        }
                    });
                } else {
                    this.setState({
                        [correspondingField]: false,
                        createUpdateParcel: {
                            ...this.state.createUpdateParcel,
                            [name]: value
                        }
                    });
                }
            } else {
                this.setState({
                    createUpdateParcel: {
                        ...this.state.createUpdateParcel,
                        [name]: value
                    }
                });
            }
        };

    private validateForm = () => {
        const {
            isAuctionEndDateValid,
            isAuctionStartDateValid,
            createUpdateParcel,
            isBidIncrementEmpty,
            isLastAcceptedBidEmpty,
            isStartingBidEmpty
        } = this.state;
        const requiredMessage = "field is required.";
        this.setState({ formErrors: [] });
        const formErrors: IErrorInfo[] = [];
        const addError = (code: string, description: string) => {
            formErrors.push({ code, description } as IErrorInfo);
        };
        if (!createUpdateParcel.name) {
            addError("EMPTY_PARCEL_NAME", "Name " + requiredMessage);
        }
        if (!createUpdateParcel.auctionStartDate) {
            addError(
                "EMPTY_AUCTION_START_DATE",
                "Start date " + requiredMessage
            );
        }
        if (!createUpdateParcel.auctionEndDate) {
            addError("EMPTY_AUCTION_END_DATE", "End date " + requiredMessage);
        }
        if (!createUpdateParcel.slug) {
            addError("EMPTY_PARCEL_SLUG", "Parcel URL " + requiredMessage);
        }
        if (!isAuctionStartDateValid) {
            addError(
                "INVALID_AUCTION_START_DATE",
                `Invalid sale start date. Date should be in ${DateTimeLocalFormat} format.`
            );
        }
        if (!isAuctionEndDateValid) {
            addError(
                "INVALID_AUCTION_END_DATE",
                `Invalid sale end date. Date should be in ${DateTimeLocalFormat} format.`
            );
        }
        if (isLastAcceptedBidEmpty) {
            addError("EMPTY_LASTACCEPTEDBID", "Sale amount " + requiredMessage);
        }
        if (isStartingBidEmpty) {
            addError("EMPTY_STARTINGBID", "Starting bid " + requiredMessage);
        }
        if (isBidIncrementEmpty) {
            addError("EMPTY_BIDINCREMENT", "Bid Increment " + requiredMessage);
        }
        this.setState({ formErrors });

        return formErrors.length === 0;
    };

    private onParcelSave = (e: React.MouseEvent) => {
        e.preventDefault();

        if (this.validateForm()) {
            if (!this.props.parcel) {
                this.props.onParcelCreate(
                    this.props.url,
                    this.state.createUpdateParcel
                );
            } else {
                const shouldUpdateBidders = !this.areArraysEqual(
                    this.state.approvedEnBlocBidders,
                    this.props.parcel.approvedEnBlocBidders?.map((p) => p.id) ??
                        []
                );
                this.props.onParcelUpdate(
                    this.props.parcel.parcelId,
                    this.state.createUpdateParcel,
                    this.state.approvedEnBlocBidders,
                    shouldUpdateBidders
                );
            }
        }
    };

    private areArraysEqual = (array1: string[], array2: string[]) => {
        if (array1.length !== array2.length) {
            return false;
        }

        const array1Copy = [...array1].sort();
        const array2Copy = [...array2].sort();

        for (let i = 0; i < array1Copy.length; i++) {
            if (array1Copy[i] !== array2Copy[i]) {
                return false;
            }
        }

        return true;
    };

    private onParcelDelete = (e: React.MouseEvent) => {
        e.preventDefault();

        if (
            this.props.parcel &&
            window.confirm(
                "Are you sure you would like to delete this parcel? This action cannot be undone."
            )
        ) {
            this.setState({ deleteClicked: true });
            this.props.onParcelDelete(
                this.props.url,
                this.props.parcel.parcelId
            );
        }
    };

    private onParcelArchive = (e: React.MouseEvent) => {
        e.preventDefault();

        const isActive = this.state.createUpdateParcel.isActive;
        if (
            this.props.parcel &&
            window.confirm(
                `Are you sure you would like to ${
                    isActive ? "archive" : "unarchive"
                } this parcel?`
            )
        ) {
            this.props.onParcelArchive(this.props.parcel.parcelId, isActive);
        }
    };

    private openErrorSnackbar = () =>
        enqueueSnackbar("Parcel successfully deleted", {
            anchorOrigin: { horizontal: "left", vertical: "top" },
            preventDuplicate: false,
            variant: "error"
        });

    private openSuccessSnackbar = () =>
        enqueueSnackbar("Parcel successfully saved", {
            anchorOrigin: { horizontal: "left", vertical: "top" },
            preventDuplicate: false,
            variant: "success"
        });

    private buildCreateUpdateParcel = (
        fallbackName?: string
    ): ICreateUpdateParcel => {
        const parcel = this.props.parcel;
        if (parcel) {
            return {
                auctionEndDate: parcel.auctionEndDate || null,
                auctionId: this.props.auction.auctionId,
                auctionStartDate: parcel.auctionStartDate || null,
                bidIncrement:
                    parcel.bidIncrement || Constants.DefaultBidIncrement,
                enBloc: parcel.enBloc || false,
                hasAcceptedBids: parcel.hasAcceptedBids || false,
                isActive: parcel.isActive ?? true,
                isPrivateSale: parcel.isPrivateSale || false,
                lastAcceptedBid: parcel.lastAcceptedBid || 0,
                name: parcel.name || fallbackName || "",
                slug:
                    parcel.slug ||
                    `parcel-${(this.props.parcelIndex ?? 0) + 1}`,
                startingBid: parcel.startingBid || 0
            };
        }
        return {
            auctionEndDate: this.getParcelEndDateTime().toString(),
            auctionId: this.props.auction.auctionId,
            auctionStartDate: this.getParcelStartDateTime().toString(),
            bidIncrement: Constants.DefaultBidIncrement,
            enBloc: false,
            hasAcceptedBids: false,
            isActive: true,
            isPrivateSale: false,
            lastAcceptedBid: 0,
            name: fallbackName || "",
            slug: `parcel-${this.props.parcelIndex ?? 1}`,
            startingBid: 0
        };
    };

    private toEditableWallTime = (isoString: string | null | undefined) => {
        const instant = parseAuctionInstant(isoString);
        return instant ? toWallTime(instant) : null;
    };

    private regularParcelsHaveNotClosedYet = () => {
        const lastRegularParcelClose =
            parseAuctionInstant(this.props.lastRegularParcelCloseDateTime) ??
            Temporal.Instant.fromEpochMilliseconds(0);
        return (
            Temporal.Instant.compare(
                lastRegularParcelClose,
                Temporal.Now.instant()
            ) > 0
        );
    };

    private getDefaultEndDateTime = (): Temporal.Instant =>
        Temporal.Now.instant().add({
            hours: Constants.DefaultHoursAfterStartTime
        });

    private getEnblocStartDateTime = (): Temporal.Instant => {
        const baseDateTime =
            parseAuctionInstant(this.props.lastRegularParcelCloseDateTime) ??
            Temporal.Now.instant();
        return baseDateTime.add({
            minutes: Constants.EnBlocStartWindowMinutes
        });
    };

    private getEnblocEndDateTime = (): Temporal.Instant =>
        this.getEnblocStartDateTime().add({
            minutes: Constants.EnBlocDurationMinutes
        });

    private getParcelStartDateTime = (): Temporal.Instant => {
        const isEnbloc = this.props.parcel?.enBloc;
        if (isEnbloc) {
            return this.getEnblocStartDateTime();
        }

        const isSynchronized = this.props.auction.isSynchronized;
        if (isSynchronized) {
            return (
                parseAuctionInstant(this.props.auction.synchronizedStartTime) ??
                Temporal.Now.instant()
            );
        }

        const existingParcelStartDateTime = this.props.parcel?.auctionStartDate;
        if (existingParcelStartDateTime) {
            return (
                parseAuctionInstant(existingParcelStartDateTime) ??
                Temporal.Now.instant()
            );
        }

        return Temporal.Now.instant();
    };

    private getParcelEndDateTime = (): Temporal.Instant => {
        const isEnbloc = this.props.parcel?.enBloc;
        if (isEnbloc) {
            return this.getEnblocEndDateTime();
        }

        const isSynchronized = this.props.auction.isSynchronized;
        if (isSynchronized) {
            return (
                parseAuctionInstant(this.props.auction.synchronizedCloseTime) ??
                this.getDefaultEndDateTime()
            );
        }

        const existingParcelCloseDateTime = this.props.parcel?.auctionEndDate;
        if (existingParcelCloseDateTime) {
            return (
                parseAuctionInstant(existingParcelCloseDateTime) ??
                this.getDefaultEndDateTime()
            );
        }

        return this.getDefaultEndDateTime();
    };
}

export default withStyles(styles)(ParcelEditContainer);
